import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useUIVersion } from '../context/UIVersionContext';
import axios from 'axios';
import { preferencesApi } from '../services/api';

const Settings = () => {
  const { user } = useAuth();
  const { version: uiVersion, setVersion: setUIVersion } = useUIVersion();
  const [formData, setFormData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false
  });
  
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(() => {
    const saved = localStorage.getItem('saver-auto-sync-enabled');
    return saved !== null ? JSON.parse(saved) : true;
  });

  useEffect(() => {
    preferencesApi.get('autoSyncEnabled').then(value => {
      if (value !== null) {
        setAutoSyncEnabled(value);
        localStorage.setItem('saver-auto-sync-enabled', JSON.stringify(value));
      }
    });
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Limpiar mensaje cuando el usuario empiece a escribir
    if (message.text) setMessage({ type: '', text: '' });
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };
  
  // Función para cambiar configuración de auto-sincronización
  const handleAutoSyncChange = (enabled) => {
    setAutoSyncEnabled(enabled);
    localStorage.setItem('saver-auto-sync-enabled', JSON.stringify(enabled));
    preferencesApi.set('autoSyncEnabled', enabled);
    setMessage({ 
      type: 'success', 
      text: `Auto-sincronización ${enabled ? 'habilitada' : 'deshabilitada'} correctamente` 
    });
  };

  const validatePasswords = () => {
    const { oldPassword, newPassword, confirmPassword } = formData;

    // Validar campos vacíos
    if (!oldPassword || !newPassword || !confirmPassword) {
      setMessage({ type: 'error', text: 'Todos los campos son requeridos' });
      return false;
    }

    // Validar longitud de contraseña
    if (newPassword.length < 8 || newPassword.length > 16) {
      setMessage({ type: 'error', text: 'La nueva contraseña debe tener entre 8 y 16 caracteres' });
      return false;
    }

    // Validar que las contraseñas coincidan
    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'Las contraseñas nuevas no coinciden' });
      return false;
    }

    // Validar que la contraseña nueva sea diferente a la actual
    if (oldPassword === newPassword) {
      setMessage({ type: 'error', text: 'La nueva contraseña debe ser diferente a la actual' });
      return false;
    }

    // Validar complejidad básica (opcional)
    const hasUpperCase = /[A-Z]/.test(newPassword);
    const hasLowerCase = /[a-z]/.test(newPassword);
    const hasNumbers = /\d/.test(newPassword);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword);

    if (!hasUpperCase || !hasLowerCase || !hasNumbers || !hasSpecialChar) {
      setMessage({ 
        type: 'error', 
        text: 'La contraseña debe contener al menos: 1 mayúscula, 1 minúscula, 1 número y 1 carácter especial' 
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validatePasswords()) return;

    setIsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await axios.post('http://localhost:9000/api/change-password', {
        oldPassword: formData.oldPassword,
        newPassword: formData.newPassword
      });

      setMessage({ type: 'success', text: response.data.message });
      
      // Limpiar formulario
      setFormData({
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Error al cambiar la contraseña';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const getPasswordStrength = (password) => {
    if (password.length === 0) return { strength: 0, text: '', color: 'bg-gray-300' };
    
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength++;

    const levels = [
      { strength: 1, text: 'Muy débil', color: 'bg-red-500' },
      { strength: 2, text: 'Débil', color: 'bg-orange-500' },
      { strength: 3, text: 'Regular', color: 'bg-yellow-500' },
      { strength: 4, text: 'Fuerte', color: 'bg-green-500' },
      { strength: 5, text: 'Muy fuerte', color: 'bg-green-600' }
    ];

    return levels[strength - 1] || { strength: 0, text: '', color: 'bg-gray-300' };
  };

  const passwordStrength = getPasswordStrength(formData.newPassword);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-2">
            Configuración
          </h1>
          <p className="text-gray-600">
            Administra la configuración de tu cuenta
          </p>
        </div>

        {/* Settings Card */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          
          {/* User Info */}
          <div className="mb-8 p-4 bg-gray-50 rounded-xl">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Información del Usuario
            </h3>
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-lg">
                  {user?.username?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
              <div>
                <p className="font-medium text-gray-800">{user?.username}</p>
                <p className="text-sm text-gray-600">Administrador</p>
              </div>
            </div>
          </div>

          {/* UI Version Selector */}
          <div className="mb-8 p-4 bg-purple-50 rounded-xl">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              Versión de la Interfaz
            </h3>
            <div className="flex items-center justify-between">
              <div className="flex-1 mr-4">
                <p className="text-sm text-gray-700 mb-1">
                  Elegí la versión del diseño que preferís
                </p>
                <p className="text-xs text-gray-500">
                  v3.0 (nueva) — diseño moderno tipo Revolut/N26 · v2.0 (clásica) — diseño original
                </p>
              </div>
              <div className="flex items-center space-x-2 bg-white rounded-full p-1 border border-purple-200">
                <button
                  type="button"
                  onClick={() => setUIVersion('v2')}
                  className={`px-4 py-1.5 text-sm font-semibold rounded-full transition-colors ${
                    uiVersion === 'v2' ? 'bg-purple-600 text-white' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  v2.0
                </button>
                <button
                  type="button"
                  onClick={() => setUIVersion('v3')}
                  className={`px-4 py-1.5 text-sm font-semibold rounded-full transition-colors ${
                    uiVersion === 'v3' ? 'bg-purple-600 text-white' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  v3.0
                </button>
              </div>
            </div>
          </div>

          {/* Auto-Sync Configuration */}
          <div className="mb-8 p-4 bg-blue-50 rounded-xl">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              Auto-Sincronización con Google Sheets
            </h3>
            <div className="flex items-center justify-between">
              <div className="flex-1 mr-4">
                <p className="text-sm text-gray-700 mb-1">
                  Sincronizar automáticamente con Google Sheets al agregar transacciones
                </p>
                <p className="text-xs text-gray-500">
                  Cuando está habilitado, las transacciones se sincronizan automáticamente 1-2 segundos después de ser agregadas
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={() => handleAutoSyncChange(!autoSyncEnabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
                    autoSyncEnabled ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                      autoSyncEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
                <span className={`text-sm font-medium ${autoSyncEnabled ? 'text-blue-600' : 'text-gray-500'}`}>
                  {autoSyncEnabled ? 'Habilitado' : 'Deshabilitado'}
                </span>
              </div>
            </div>
          </div>

          {/* Change Password Form */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-6 flex items-center">
              Cambiar Contraseña
            </h3>

            <form onSubmit={handleSubmit} className="space-y-6">
              
              {/* Message */}
              {message.text && (
                <div className={`p-4 rounded-lg flex items-center space-x-2 ${
                  message.type === 'success' 
                    ? 'bg-green-50 border border-green-200' 
                    : 'bg-red-50 border border-red-200'
                }`}>
                  <span className={message.type === 'success' ? 'text-green-600' : 'text-red-600'}></span>
                  <p className={`text-sm ${
                    message.type === 'success' ? 'text-green-700' : 'text-red-700'
                  }`}>
                    {message.text}
                  </p>
                </div>
              )}

              {/* Current Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contraseña Actual
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.old ? 'text' : 'password'}
                    name="oldPassword"
                    value={formData.oldPassword}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 pr-12"
                    placeholder="Ingresa tu contraseña actual"
                    disabled={isLoading}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('old')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPasswords.old ? 'Ocultar' : 'Ver'}
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nueva Contraseña
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.new ? 'text' : 'password'}
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 pr-12"
                    placeholder="Ingresa tu nueva contraseña"
                    disabled={isLoading}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('new')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPasswords.new ? 'Ocultar' : 'Ver'}
                  </button>
                </div>
                
                {/* Password Strength Indicator */}
                {formData.newPassword && (
                  <div className="mt-2">
                    <div className="flex items-center space-x-2 mb-1">
                      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${passwordStrength.color} transition-all duration-300`}
                          style={{ width: `${(passwordStrength.strength / 5) * 100}%` }}
                        ></div>
                      </div>
                      <span className="text-xs text-gray-600">{passwordStrength.text}</span>
                    </div>
                    <p className="text-xs text-gray-500">
                      8-16 caracteres, mayúsculas, minúsculas, números y símbolos
                    </p>
                  </div>
                )}
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirmar Nueva Contraseña
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.confirm ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 pr-12"
                    placeholder="Confirma tu nueva contraseña"
                    disabled={isLoading}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('confirm')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPasswords.confirm ? 'Ocultar' : 'Ver'}
                  </button>
                </div>
                
                {/* Password Match Indicator */}
                {formData.confirmPassword && (
                  <div className="mt-2">
                    {formData.newPassword === formData.confirmPassword ? (
                      <p className="text-xs text-green-600 flex items-center">
                          Las contraseñas coinciden
                      </p>
                    ) : (
                      <p className="text-xs text-red-600 flex items-center">
                          Las contraseñas no coinciden
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full py-3 px-4 rounded-xl font-semibold text-white transition-all duration-200 ${
                  isLoading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
                }`}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Cambiando contraseña...</span>
                  </div>
                ) : (
                  <span>Cambiar Contraseña</span>
                )}
              </button>

            </form>
          </div>

          {/* Security Info */}
          <div className="mt-8 p-4 bg-blue-50 rounded-xl">
            <h4 className="text-sm font-semibold text-blue-800 mb-2">
              Información de Seguridad
            </h4>
            <ul className="text-xs text-blue-700 space-y-1">
              <li>• Las contraseñas están protegidas con encriptación bcrypt</li>
              <li>• Los tokens JWT expiran automáticamente en 2 horas</li>
              <li>• Se requiere autenticación para cambiar la contraseña</li>
              <li>• Los cambios se aplican inmediatamente</li>
            </ul>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Settings; 